// Plain-language copy for the enrichment editor. Keeps every operator-facing
// string out of the widget component so wording is reviewable in one place and
// never leaks schema field names (approachable brand layer). No schema change:
// EnrichmentDraft is a partial EnrichmentSpec while a row is being edited.

export type EnrichmentKind = "snowflake-view" | "profile-field";

export interface EnrichmentDraft {
  outputField?: string;
  kind: EnrichmentKind;
  instanceId?: string;
  sourceKeyPath?: string;
  required?: boolean;
  view?: string;
  keyColumn?: string;
  valueColumn?: string;
  keyType?: "string" | "number";
  profileField?: string;
}

/** kind → the connector type whose instances can satisfy it, for the
 *  "Which connection?" picker. */
export const CONNECTOR_TYPE_FOR_KIND: Record<EnrichmentKind, string> = {
  "snowflake-view": "snowflake-source",
  "profile-field": "jump-profile-dest",
};

/** discover `kind` argument used to populate each dropdown once a connector
 *  implements discover (dormant until then; the widget degrades gracefully). */
export const DISCOVER_KIND = {
  view: "view",
  column: "column",
  profileField: "field",
} as const;

/** Common form-field keys offered as suggestions for "which field identifies
 *  the fan" — forms are custom so this is a hint list, not an enum. */
export const SOURCE_KEY_SUGGESTIONS = ["fanProfileId", "orderId", "formId", "submissionId"];

export const QUESTION = {
  outputField: "What should we call this piece of info?",
  kind: "Where does it come from?",
  instanceId: "Which connection should we use?",
  sourceKeyPath: "Which field on the form identifies the record?",
  view: "Which data table should we read?",
  keyColumn: "Which column in that table do we match on?",
  valueColumn: "Which column holds the value to bring back?",
  keyType: "Is that matching column a number?",
  profileField: "Which profile detail should we pull?",
  required: "Required — if we can't find it, hold the message and retry",
} as const;

export const KIND_LABEL: Record<EnrichmentKind, string> = {
  "snowflake-view": "a data table",
  "profile-field": "a fan's profile",
};

/** One-line summary for a collapsed row. Tolerates a partial draft. */
export function describeEnrichment(e: EnrichmentDraft): string {
  const name = e.outputField?.trim() || "New lookup";
  const where = e.kind === "snowflake-view" ? "look it up in a data table" : "pull it from the fan's profile";
  const match = e.sourceKeyPath?.trim() ? `, matched by ${e.sourceKeyPath.trim()}` : "";
  const req = e.required ? " · REQUIRED" : "";
  return `Add ${name} — ${where}${match}${req}`;
}
